/**
 * Diagnostic Templates
 *
 * Business impact explanations and Claude Code context templates for all monitored services.
 * Used by ServiceHealthCard to display meaningful information when services are degraded.
 */

/**
 * Format a Firestore timestamp or Date for display
 * @param {object|Date|null} timestamp - Firestore Timestamp, Date, or null
 * @returns {string} Formatted date string or 'Never'
 */
function formatTimestamp(timestamp) {
  if (!timestamp) return 'Never';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString();
}

/**
 * Diagnostic templates for all monitored services
 * Keyed by service ID matching serviceRegistry.js
 */
export const DIAGNOSTIC_TEMPLATES = {
  spApi: {
    businessImpact: {
      yellow: 'Amazon product syncing is slower than expected. New listings or inventory updates may be delayed.',
      red: 'Amazon integration is not working. No products can be listed or updated on Amazon until fixed.'
    },
    claudeCodeContext: {
      template: `PROBLEM: Amazon SP-API Issue
Service: Amazon SP-API
Status: {status} - {statusReason}
Auth Valid: {authValid}
Rate Limit Hits (24h): {rateLimitHits}
Last Success: {lastSuccessAt}

WHAT HAPPENED:
{statusReason}

This affects all Amazon operations including:
- Product listing creation and updates
- Inventory quantity synchronization
- Pricing changes
- Order data retrieval

CODE LOCATION:
- Repo: mcv2
- Path: functions/integrations/amazon/
- Key Files: AsinSearchService.js, SpApiClient.js

FIRESTORE:
- Health doc: systemHealth/services/spApi/current
- Errors: systemHealth/services/spApi/errors/recent

RECENT ERRORS:
{recentErrors}

TO FIX:
If auth expired (authValid = false):
1. Go to Amazon Seller Central -> Apps & Services -> Manage Apps
2. Find "Merlin" app and click "Reauthorize"
3. Complete OAuth flow to get new refresh token
4. Update AMAZON_SP_API_REFRESH_TOKEN in Firebase Functions config
5. Redeploy functions: firebase deploy --only functions

If rate limited:
1. Wait 15-30 minutes for rate limit to reset
2. Check for runaway processes making too many calls
3. Review functions/integrations/amazon/ for recent changes`,
      checkList: [
        'Check authValid field in health data',
        'Review rateLimitHits count',
        'Check Amazon Seller Central for account issues',
        'Verify SP-API credentials in Firebase config',
        'Check for recent code deployments that may have caused issues'
      ]
    }
  },

  shopify: {
    businessImpact: {
      yellow: 'Some product updates are failing to reach Shopify. Customers may see outdated information.',
      red: 'Shopify sync is completely stopped. No product changes will appear in your store until fixed.'
    },
    claudeCodeContext: {
      template: `PROBLEM: Shopify Sync Issue
Service: Shopify
Status: {status} - {statusReason}
Push Errors (24h): {pushErrorCount}
Last Success: {lastSuccessAt}

WHAT HAPPENED:
{statusReason}

This affects all Shopify updates including:
- Product titles and descriptions
- Prices and compare-at prices
- Inventory quantities
- Product images

CODE LOCATION:
- Repo: mcv2
- Path: functions/integrations/shopify/
- Key Files: pushToShopify.js, ShopifyService.js

FIRESTORE:
- Health doc: systemHealth/services/shopify/current
- Failed jobs: productCatalog/topicz/enrichmentJobs (status=pushed, has pushErrors)

RECENT ERRORS:
{recentErrors}

TO FIX:
1. Check Shopify API status: status.shopify.com
2. Review recent push errors in Firestore enrichmentJobs collection
3. Common issues:
   - Rate limit: Wait 5-10 minutes and retry
   - Invalid data: Check product has required fields (title, price)
   - API key: Verify SHOPIFY_ACCESS_TOKEN in Firebase config
4. If API key issue: Regenerate token in Shopify Admin -> Apps -> Develop Apps`,
      checkList: [
        'Check Shopify status page for outages',
        'Review pushErrorCount in health data',
        'Check enrichmentJobs with pushErrors in Firestore',
        'Verify Shopify API credentials',
        'Look for products with missing required fields'
      ]
    }
  },

  shipStation: {
    businessImpact: {
      yellow: 'Shipping label sync is running slowly. Recent orders may have delayed shipping.',
      red: 'Shipping sync has stopped. Orders will not get shipping labels until the connection is restored.'
    },
    claudeCodeContext: {
      template: `PROBLEM: ShipStation Sync Stalled
Service: ShipStation
Status: {status} - {statusReason}
Last Sync: {lastSuccessAt}
Minutes Since Sync: {minutesSinceSuccess}
Recent Batches: {recentBatchCount}

WHAT HAPPENED:
{statusReason}

The EC2 worker that syncs fulfillments to ShipStation hasn't reported
activity recently. This could mean the worker crashed, the API is down,
or there's a network issue.

This affects:
- Shipping label creation for new orders
- Tracking number updates
- Order fulfillment status

CODE LOCATION:
- Repo: merlin-core (EC2 worker, separate repository)
- Path: batchSync.js
- Server: 50.19.181.177

FIRESTORE:
- Batch state: batchState (most recent doc shows last sync)

RECENT ERRORS:
{recentErrors}

TO FIX:
1. SSH to EC2: ssh merlin@50.19.181.177
2. Check worker status: pm2 status
3. Check logs: pm2 logs batchSync --lines 100
4. If crashed, restart: pm2 restart batchSync
5. If API issue, check ShipStation status page
6. Verify ShipStation API key hasn't expired`,
      checkList: [
        'Check if EC2 worker is running (pm2 status)',
        'Review pm2 logs for errors',
        'Verify batchState document in Firestore',
        'Check ShipStation status page',
        'Verify ShipStation API credentials'
      ]
    }
  },

  sellerActive: {
    businessImpact: {
      yellow: 'Cart.com/SellerActive inventory sync is experiencing some errors. Multi-channel inventory may be briefly out of sync.',
      red: 'SellerActive integration is down. Inventory will not sync across marketplaces until fixed.'
    },
    claudeCodeContext: {
      template: `PROBLEM: SellerActive/Cart.com Sync Issue
Service: SellerActive (Cart.com)
Status: {status} - {statusReason}
Recent Errors (24h): {recentErrorCount}
Last Success: {lastSuccessAt}

WHAT HAPPENED:
{statusReason}

This affects multi-channel inventory synchronization:
- Inventory quantities across marketplaces
- Out-of-stock prevention
- Overselling protection

CODE LOCATION:
- Repo: mcv2
- Path: functions/index.js (updateCartComInventory function)
- Also: functions/integrations/selleractive/ (if exists)

FIRESTORE:
- Health doc: systemHealth/services/sellerActive/current
- Errors: systemHealth/services/sellerActive/errors/recent

RECENT ERRORS:
{recentErrors}

TO FIX:
1. Check Cart.com API status
2. Verify SellerActive API key in Firebase config:
   - SELLER_ACTIVE_API_KEY
   - SELLER_ACTIVE_MERCHANT_ID
3. Review error responses for specific failure reasons
4. Common issues:
   - API key expired: Regenerate in Cart.com dashboard
   - Rate limit: Wait and retry
   - Invalid SKU: Check product data mapping`,
      checkList: [
        'Check Cart.com API status',
        'Review recentErrorCount in health data',
        'Verify SellerActive API credentials',
        'Check for invalid SKU mappings',
        'Review recent Firestore errors for patterns'
      ]
    }
  }
};

/**
 * Get business impact explanation for a service/status combination
 * @param {string} serviceId - Service ID (spApi, shopify, shipStation, sellerActive)
 * @param {string} status - Status level (green, yellow, red, unknown)
 * @returns {string|null} Business impact explanation or null if not applicable
 */
export function getBusinessImpact(serviceId, status) {
  const template = DIAGNOSTIC_TEMPLATES[serviceId];
  if (!template) return null;

  // Only return impact for degraded states
  if (status === 'yellow') {
    return template.businessImpact.yellow;
  }
  if (status === 'red') {
    return template.businessImpact.red;
  }

  // Green, unknown, or other states don't need impact explanation
  return null;
}

/**
 * Generate Claude Code context block for a service
 * Fills in template placeholders with actual health data
 * @param {string} serviceId - Service ID
 * @param {object} healthData - Health data from Firestore
 * @param {Array} recentErrors - Array of recent error objects (optional)
 * @returns {string|null} Filled context string or null if service not found
 */
export function generateClaudeCodeContext(serviceId, healthData = {}, recentErrors = []) {
  const template = DIAGNOSTIC_TEMPLATES[serviceId];
  if (!template) return null;

  // Format recent errors for display
  let errorsText = 'No recent errors recorded';
  if (recentErrors && recentErrors.length > 0) {
    errorsText = recentErrors
      .slice(0, 5) // Limit to 5 most recent
      .map((err, i) => {
        const timestamp = err.timestamp ? formatTimestamp(err.timestamp) : 'Unknown time';
        const message = err.message || err.error || err.type || 'Unknown error';
        return `${i + 1}. [${timestamp}] ${message}`;
      })
      .join('\n');
  }

  // Replace all placeholders with actual values
  let context = template.claudeCodeContext.template
    .replace(/{status}/g, healthData.status || 'unknown')
    .replace(/{statusReason}/g, healthData.statusReason || 'No reason provided')
    .replace(/{lastSuccessAt}/g, formatTimestamp(healthData.lastSuccessAt))
    .replace(/{minutesSinceSuccess}/g, healthData.minutesSinceSuccess ?? 'N/A')
    .replace(/{recentErrors}/g, errorsText)
    // Service-specific fields
    .replace(/{authValid}/g, healthData.authValid ?? 'unknown')
    .replace(/{rateLimitHits}/g, healthData.rateLimitHits ?? 0)
    .replace(/{pushErrorCount}/g, healthData.pushErrorCount ?? 0)
    .replace(/{recentBatchCount}/g, healthData.recentBatchCount ?? 0)
    .replace(/{recentErrorCount}/g, healthData.recentErrorCount ?? 0);

  return context;
}

/**
 * Get troubleshooting checklist for a service
 * @param {string} serviceId - Service ID
 * @returns {Array|null} Array of troubleshooting steps or null
 */
export function getTroubleshootingChecklist(serviceId) {
  const template = DIAGNOSTIC_TEMPLATES[serviceId];
  if (!template) return null;
  return template.claudeCodeContext.checkList;
}
